import { useState } from 'react';
import {
    Column,
    Item,
    Lookup
} from 'devextreme-react/data-grid';
import { Buffer } from 'buffer';
import { useGetRetailOrders } from "../api/get-retail-orders";
import { RetailOrderGridProps } from '../types/RetailOrderGridProps';
import { useGetOrderStatuses } from '../../../../api/lookup';
import { Link, useLocation } from 'react-router-dom';
import { LoadPagePanel, SyncDataGrid } from '../../../../components';
import { ClosedOrdersControl } from '../../../common';
import { showErrorMessage } from '../../../../utils/messages';
import { RetailOrderReportType } from '../../../cfi/retail-order';
import { apiGenerateRetailOrderReport } from '../../../common/retail-order-report/api/get-retail-order-report';

export default function RetailOrderGrid(props: RetailOrderGridProps) {

    let [closedOrdersState, setClosedOrdersState] = useState<{ closedOrders: boolean, dateFrom: Date | null }>
        ({ closedOrders: false, dateFrom: null });

    let [loadingReport, setLoadingReport] = useState(false);

    const orders = useGetRetailOrders(props.vanityOrders, props.imeOrders, closedOrdersState.closedOrders, closedOrdersState.dateFrom);
    const orderStatuses = useGetOrderStatuses();

    const location = useLocation();
    const returnUrl = encodeURIComponent(location.pathname + location.search);

    return (
        <>
            <SyncDataGrid
                stateStorageKey={'dealer-retail-order-grid-v3' + props.vanityOrders + '-' + props.imeOrders}
                className={'dx-card wide-card'}
                dataSource={orders.data}
                keyExpr={'retailOrderID'}
                height="60vh"
                additionalToolbarItems={[
                    <Item
                        location={'before'}
                        key="closed-orders"
                    >
                        <ClosedOrdersControl
                            onClosedOrdersChanged={(e) => {
                                setClosedOrdersState({ closedOrders: e.checked, dateFrom: e.dateFrom })
                            }}
                        />
                    </Item>]
                }
            >
                <Column
                    dataField={'roNumber'}
                    caption={'Order #'}
                    dataType="string"
                    cellRender={(cell: any) => {
                        return <Link to={`/dealer/retail-order/${cell.data.retailOrderID}?returnUrl=${returnUrl}`}>
                            {cell.data.roNumber}
                        </Link>;
                    }}
                />
                <Column
                    dataField={'projectNumber'}
                    caption={'Project #'}
                    dataType="string"
                />
                <Column
                    dataField={'roDate'}
                    caption={'Order Date'}
                    dataType="date"
                />
                <Column
                    dataField={'createdOn'}
                    caption={'RO Creation Date'}
                    dataType="datetime"
                />
                <Column
                    dataField={'orderType'}
                    caption={'Order Type'}
                    dataType="string"
                />
                <Column
                    dataField={'dealerNumber'}
                    caption={'Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'orderStatus'}
                    caption={'Order Status'}
                    dataType="string"
                >
                    <Lookup
                        dataSource={orderStatuses.data}
                        valueExpr={'description'}
                        displayExpr={'description'}
                    />
                </Column>
                <Column
                    dataField={'productLine'}
                    caption={'Product Line'}
                    dataType="string"
                />
                {
                    !props.vanityOrders &&
                    <Column
                        dataField={'enterTemplateDate'}
                        caption={'Template Date'}
                        dataType="date"
                    />
                }
                <Column
                    dataField={'scheduledInstallDate'}
                    caption={'Scheduled Install Date'}
                    dataType="date"
                />
                <Column
                    dataField={'actualInstallDate'}
                    caption={'Actual Install Date'}
                    dataType="date"
                />
                <Column
                    dataField={'scheduledDeliveryDate'}
                    caption={'Scheduled Delivery Date'}
                    dataType="date"
                />
                <Column
                    dataField={'deliveryDate'}
                    caption={'Actual Delivery Date'}
                    dataType="date"
                />
                <Column
                    dataField={'customerName'}
                    caption={'Customer Name'}
                    dataType="string"
                />
                <Column
                    dataField={'customerAddress'}
                    caption={'Customer Address'}
                    dataType="string"
                />
                <Column
                    dataField={'customerPhone'}
                    caption={'Customer Phone #'}
                    dataType="string"
                />
                <Column
                    dataField={'customerEmail'}
                    caption={'Customer Email'}
                    dataType="string"
                />
                <Column
                    dataField={'customerCityZip'}
                    caption={'City/State,Zip'}
                    dataType="string"
                />
                <Column
                    dataField={'cfiCompanyName'}
                    caption={'CFI'}
                    dataType="string"
                />
                {
                    props.imeOrders &&
                    <Column
                        dataField={'imeStatus'}
                        caption={'IME Status'}
                        dataType="string"
                    />
                }
                {
                    props.vanityOrders &&
                    <Column
                        dataField={'vanityOrderFormUploaded'}
                        caption={'Vanity Order Form Uploaded'}
                        dataType="boolean"
                    />
                }
                <Column
                    dataField={'hasChangeRequest'}
                    caption={'Change Request'}
                    dataType="string"
                    cellRender={(cell: any) => {
                        return cell.data.hasChangeRequest ? <a href=""
                            onClick={(e) => {

                                e.preventDefault();

                                setLoadingReport(true);

                                apiGenerateRetailOrderReport(cell.data.retailOrderID, RetailOrderReportType.ChangeRequestDetails)
                                    .then((result) => {
                                        if (result.isSuccess) {
                                            const file = new Blob(
                                                [Buffer.from(result.data, "base64")],
                                                { type: 'application/pdf' });

                                            const fileURL = URL.createObjectURL(file);
                                            window.open(fileURL);
                                        }
                                        else {
                                            showErrorMessage(result.errorMessage!);
                                        }
                                    })
                                    .catch((reason) => {
                                        showErrorMessage(reason);
                                    })
                                    .finally(() => { setLoadingReport(false); });
                            }}
                        >Change Request</a> : "";
                    }}
                />
                <Column
                    dataField={'colorName'}
                    caption={'Color Name'}
                    dataType="string"
                    visible={false}
                />
            </SyncDataGrid>

            <LoadPagePanel visible={orders.isLoading || loadingReport} />
        </>
    )
} 
